import clsx from 'clsx';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import Button from '@/src/components/common/Elements/Button/Button';
import Icon, { ICON_TYPES, ICONS } from '@/src/components/common/Elements/Icon/Icon';
import SectionLoader from '@/src/components/common/Elements/Spinner/SectionLoader/SectionLoader';
import useSearchParamsState from '@/src/hooks/useSearchParamsState';
import { RootState } from '@/src/reducers/types';

import { useInboxEntries } from './api/getInboxEntries';
import InboxEntries from './InboxEntries/InboxEntries';
import { mapInboxEntry } from './InboxEntry/InboxEntry';
import SidebarLayout from './SidebarLayout';

import { useInboxEntryPanel } from '../../store/inboxEntryPanel.store';
import { InboxEntry, StatusFilterEnum, View } from '../../types';

type SidebarProps = {
  filter: StatusFilterEnum;
  view: View;
};

export const mapInboxEntries = (responseData): InboxEntry[] => {
  return responseData.pages.flatMap((page) => page.data.map((r) => mapInboxEntry(r)));
};

const Sidebar = ({ filter, view }: SidebarProps) => {
  const isSidePanelCollapsed = useSelector((store: RootState) => store.common.isSidePanelCollapsed);
  const searchTerm = useSelector((store: RootState) => store.filters.searchTerm);
  const sidebarRef = useRef(null);
  const { projectId } = useParams<{ projectId: string }>();
  const { setInboxEntry } = useInboxEntryPanel();
  const [inboxEntries, setInboxEntries] = useState<InboxEntry[]>([]);

  const inboxEntriesQuery = useInboxEntries({
    projectId,
    filter: view === View.Trash ? View.Trash : filter,
    search: searchTerm,
  });

  const [searchParams] = useSearchParamsState();
  const preselectedInboxEntryId = searchParams.get('id');
  const isTrash = view === View.Trash;

  const sidebarClassName = useMemo(
    () => (isSidePanelCollapsed ? 'tw-max-w-[580px]' : 'tw-max-w-[465px]'),
    [isSidePanelCollapsed]
  );

  useEffect(() => {
    if (inboxEntries.length < 1) {
      return;
    }

    const preselectedInboxEntryIndex = inboxEntries.findIndex(
      (ie) => ie.data.id === +preselectedInboxEntryId
    );

    if (preselectedInboxEntryIndex >= 0) {
      setInboxEntry(inboxEntries[preselectedInboxEntryIndex]);
    }
  }, [inboxEntries, preselectedInboxEntryId, setInboxEntry]);

  useEffect(() => {
    if (!inboxEntriesQuery.data || !inboxEntriesQuery.data.pages) {
      // setInboxEntries([]);
      return;
    }

    const mappedInboxEntries = mapInboxEntries(inboxEntriesQuery.data);

    setInboxEntries(mappedInboxEntries);
  }, [inboxEntriesQuery.data]);

  // Uncomment this if we want to clear an inboxEntry if its not present in the sidebar
  // Each change to source list should update selected inbox entry
  // useEffect(() => {
  //   if (!selectedInboxEntry) {
  //     return;
  //   }

  //   const updatedInboxEntry = inboxEntries.find((ie) => ie.data.id === selectedInboxEntry.data.id);
  //   setInboxEntry(updatedInboxEntry);
  // }, [selectedInboxEntry, setInboxEntry, inboxEntries]);

  if (inboxEntriesQuery.isLoading || !inboxEntriesQuery.data) {
    return (
      <SidebarLayout sidebarRef={sidebarRef} className={sidebarClassName}>
        <SectionLoader
          className={clsx(
            'tw-flex tw-flex-col tw-items-center tw-justify-center',
            'tw-h-full',
            'tw-px-[10px]'
          )}
        />
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout sidebarRef={sidebarRef} className={sidebarClassName}>
      {!inboxEntriesQuery.isLoading && searchTerm && (
        <div className={clsx('tw-bg-cfo-yellow-100', 'tw-py-[8px]', 'tw-px-[15px]')}>
          <p
            className={clsx(
              'tw-leading-[14px]',
              'tw-text-[12px]',
              'tw-text-cfo-black',
              'tw-font-brandonTextRegular'
            )}
          >
            ({inboxEntries.length}) search results for{' '}
            <span className={clsx('tw-font-brandonTextMedium')}>&quot;{searchTerm}&quot;</span>{' '}
          </p>
        </div>
      )}
      {isTrash && (
        <div
          className={clsx(
            'tw-group',
            'tw-sticky tw-top-0 tw-z-[1]',
            'tw-flex tw-items-center tw-justify-center',
            'tw-text-[12px] tw-text-cfo-black tw-font-brandonTextMedium',
            'tw-p-[10px]',
            'tw-bg-cfo-grey-500'
          )}
        >
          <Icon
            className={clsx('tw-text-[18px] tw-mr-[5px]')}
            icon={ICONS.info}
            iconType={ICON_TYPES.outlined}
          />
          Deleted entries. Permanently remove them or restore it to Inbox.
        </div>
      )}
      {/* If inbox entries is empty */}
      {inboxEntries.length === 0 && (
        <div
          className={clsx(
            'tw-flex tw-flex-col tw-items-center tw-justify-center',
            'tw-h-full',
            'tw-px-[10px]'
          )}
        >
          <p
            className={clsx(
              'tw-text-[18px] tw-text-cfo-grey-600 tw-font-brandonTextMedium',
              'tw-leading-[20px]'
            )}
          >
            Your Inbox is empty
          </p>
          <p className={clsx('tw-text-cfo-grey-600 tw-leading-[20px]')}>
            All emails sent and files uploaded to this account will show up here.
          </p>
        </div>
      )}
      <InboxEntries
        inboxEntries={inboxEntries}
        filter={filter}
        view={view}
        projectId={projectId}
        sidebarRef={sidebarRef}
        isSearchActive={!!searchTerm}
      >
        {inboxEntriesQuery.isFetchingNextPage && <SectionLoader />}

        {!inboxEntriesQuery.isFetchingNextPage && inboxEntriesQuery.hasNextPage && (
          <Button
            className={clsx(
              'tw-w-full',
              'tw-p-[15px] tw-m-0',
              'tw-text-cfo-blue-600 tw-font-brandonTextMedium tw-underline',
              'hover:tw-underline'
            )}
            onClick={inboxEntriesQuery.fetchNextPage}
          >
            Load More
          </Button>
        )}
      </InboxEntries>
    </SidebarLayout>
  );
};

export default Sidebar;
